import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Col, Container, Form, Row } from 'react-bootstrap';
import {
  ActionButtons,
  addStatusToast,
  DataTableItem,
  Loader,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getAllPurchaselAccountingGroupsAsync,
  AccountingsQueryFilters,
  AccountingGroupDetails,
  selectPurchaseAccountingGroups,
  selectPurchaseAccountingGroupsLoadingStatus,
  updatePurchaseAccountingGroupAsync,
  selectSellVats,
  selectPurchaseVats,
  getAllSellVatsAsync,
  getAllPurchaseVatsAsync,
} from './accountingSlice';
import { LOADING_STATUS } from '../../common/constants';

const PurchaseAccountingGroupDetailsView = () => {
  const backLinkUrl = '/accounting/purchaseaccountinggroups';
  const params = useParams();
  const dispatch = useAppDispatch();
  const accountingGroups = useAppSelector(selectPurchaseAccountingGroups);
  const accountingGroupsLoadingStatus = useAppSelector(
    selectPurchaseAccountingGroupsLoadingStatus,
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const purchasevats = useAppSelector(selectPurchaseVats);

  const [purchasevatsList, setPurchasevatsList] = useState<DataTableItem[]>([]);
  const [filtering, setFiltering] = useState<AccountingsQueryFilters>({
    pageSize: 200,
    pageNumber: 1,
    culture: 'fi_FI',
  });

  const [editedAccountingAccountsData, setEditedAccountingAccountsData] =
    useState<AccountingGroupDetails>({
      sellVatId: '',
      id: '',
      name: '',
      advancePurchaseAccount: 0,
      discountAccount: 0,
      advanceSalesAccount: 0,
      domesticTravelAgencyCommissionAccount: 0,
      purchaseAccount: 0,
      foreignTravelAgencyCommissionAccount: 0,
      purchaseDeptAccount: 0,
      purchasesWithoutTaxesAccount: 0,
      purchaseVatDeptAccount: 0,
      salesAccount: 0,
      salesClaimAccount: 0,
      salesVatDeptAccount: 0,
      sellerCommissionAccount: 0,
      purchaseVatId: '',
    });

  const initFetch = useCallback(() => {
    dispatch(getAllPurchaseVatsAsync(filtering));
    dispatch(getAllPurchaselAccountingGroupsAsync(filtering));
  }, [dispatch]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);

  useEffect(() => {
    if (purchasevats && purchasevats.length > 0) {
      const formattedSelVatsList: DataTableItem[] = [];
      purchasevats?.forEach(item => {
        formattedSelVatsList.push({
          ItemId: item.id,
          Items: [item.name],
          Url: `/accounting/purchasevats/details/${item.id}`,
        });
      });
      setPurchasevatsList(formattedSelVatsList);
    }
  }, [purchasevats]);

  // Choose group to edit by Id
  useEffect(() => {
    if (params.accountingGroupId && accountingGroups.length > 0) {
      const getAccountingGroupId = _id =>
        accountingGroups.find(({ id }) => id === params.accountingGroupId);
      if (getAccountingGroupId) {
        setEditedAccountingAccountsData(getAccountingGroupId);
      }
    }
  }, [params.accountingGroupId, accountingGroups, accountingGroups]);

  // Set view loading status
  useEffect(() => {
    if (
      accountingGroupsLoadingStatus !== LOADING_STATUS.LOADING &&
      editedAccountingAccountsData != null &&
      editedAccountingAccountsData.id !== ''
    ) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [accountingGroupsLoadingStatus, editedAccountingAccountsData]);

  const purchaseVatsOptions = [
    { ItemId: '', Items: [''], Url: '' } as DataTableItem,
  ]
    .concat(purchasevatsList)
    .map(item => (
      <option key={item.ItemId} value={item.ItemId} id={item.ItemId}>
        {item.Items}
      </option>
    ));

  const handleChangeAccountingGroupPurcahseVat = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const sellVatGuid = purchaseVatsOptions.filter(
      itemv => value === itemv.key,
    );
    if (sellVatGuid != null) {
      const storededitedGroup: AccountingGroupDetails = {
        ...editedAccountingAccountsData,
        purchaseVatId: sellVatGuid[0].key.toString(),
      };
      setEditedAccountingAccountsData(storededitedGroup);
    } else {
      const storededitedGroup = {
        ...editedAccountingAccountsData,
        purchaseVatId: null,
      };
      setEditedAccountingAccountsData(storededitedGroup);
    }
  };
  const handleChangeAccountingAccounts = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (editedAccountingAccountsData != null) {
      const storedAccountingAccountsData = {
        ...editedAccountingAccountsData,
        [event.target.name]: value,
      };
      setEditedAccountingAccountsData(storedAccountingAccountsData);
    }
  };

  const saveAccountingAccounts = () => {
    dispatch(
      updatePurchaseAccountingGroupAsync(editedAccountingAccountsData),
    ).then(res3 => {
      dispatch(
        addStatusToast({
          Message: `Asetukset tallennettu onnistuneesti`,
        }),
      );
    });
  };

  return (
    <>
      <Helmet>
        <title>Kirjanpitotilit - Määrityksen tiedot</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Kirjanpito ja Alv-määritykset', 'Kirjanpitotilit']}
          Title={`Kirjanpitotilin tiedot: ${
            editedAccountingAccountsData != null
              ? editedAccountingAccountsData.name
              : ''
          }`}
        />

        <Loader IsLoading={isLoading} />

        {!isLoading && (
          <Container className="container-form mt-48">
            <Form.Group controlId="name" className="mb-40">
              <Form.Label>Nimi</Form.Label>
              <Form.Control
                autoComplete="off"
                value={editedAccountingAccountsData.name}
                onChange={handleChangeAccountingAccounts}
                name="name"
              />
            </Form.Group>

            <Row xs={1} sm={2}>
              <Col>
                <Form.Group controlId="purchaseAccount" className="mb-40">
                  <Form.Label>Ostot</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={editedAccountingAccountsData.purchaseAccount}
                    onChange={handleChangeAccountingAccounts}
                    name="purchaseAccount"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group
                  controlId="advancePurchaseAccount"
                  className="mb-40"
                >
                  <Form.Label>Ostoennakot</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={editedAccountingAccountsData.advancePurchaseAccount}
                    onChange={handleChangeAccountingAccounts}
                    name="advancePurchaseAccount"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group
                  controlId="purchasesWithoutTaxesAccount"
                  className="mb-40"
                >
                  <Form.Label>Ostot 0 %</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={
                      editedAccountingAccountsData.purchasesWithoutTaxesAccount
                    }
                    onChange={handleChangeAccountingAccounts}
                    name="purchasesWithoutTaxesAccount"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group
                  controlId="purchaseVatDeptAccount"
                  className="mb-40"
                >
                  <Form.Label>Ostojen alv-saamiset</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={editedAccountingAccountsData.purchaseVatDeptAccount}
                    onChange={handleChangeAccountingAccounts}
                    name="purchaseVatDeptAccount"
                  />
                </Form.Group>
                <Form.Group controlId="accountinggroup" className="mb-40">
                  <Form.Label>Alv %</Form.Label>
                  {purchaseVatsOptions.length > 0 && (
                    <Form.Select
                      value={editedAccountingAccountsData.purchaseVatId}
                      onChange={handleChangeAccountingGroupPurcahseVat}
                      name="sellVatsOptions"
                    >
                      {purchaseVatsOptions}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <ActionButtons
              CancelButtonText="Peruuta"
              CancelButtonUrl={backLinkUrl}
              HandleSave={saveAccountingAccounts}
            />
          </Container>
        )}
      </Container>
    </>
  );
};

export default PurchaseAccountingGroupDetailsView;
